import React, { useContext, useEffect, useState } from 'react';
import { API } from 'aws-amplify';
import operationData from '../../../../../Operation/data.json';
import { Context } from '../../../../Context';
import FoodDeliveryTabs from './FoodDeliveryTabs';

const API_NAME = 'foodDelivery';
const INDEX = '0';

const DeliveryPrice = () => {
  const { institutionData } = useContext(Context);
  const institution = operationData.foodDeliveryInstitution;
  const activeInstitution = institution || institutionData?.institution || '';

  const [ranges, setRanges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newRange, setNewRange] = useState({ minKm: '', maxKm: '', price: '' });
  const [error, setError] = useState('');

  const fetchRanges = async () => {
    if (!activeInstitution) return;
    setLoading(true);
    setError('');
    try {
      const path = `/admin/settings/${activeInstitution}/${INDEX}`;
      const res = await API.get(API_NAME, path, { queryStringParameters: { type: 'delivery' } });
      const data = res?.data || res || [];
      setRanges(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || 'Failed to load delivery prices.');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchRanges(); }, [activeInstitution]);

  const addRange = () => {
    const max = Number(newRange.maxKm);
    const price = Number(newRange.price);
    if (isNaN(max) || isNaN(price)) return setError('Invalid numbers');
    const min = ranges.length > 0 ? Number(ranges[ranges.length - 1].maxKm) : 0;
    if (min >= max) return setError('Max must be greater than previous max');
    setRanges([...ranges, { minKm: min, maxKm: max, price }]);
    setNewRange({ minKm: '', maxKm: '', price: '' });
  };

  const removeRange = (i) => setRanges(ranges.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!activeInstitution) return;
    setLoading(true);
    setError('');
    try {
      const path = `/admin/settings/${activeInstitution}/${INDEX}`;
      await API.post(API_NAME, path, { queryStringParameters: { type: 'delivery' }, body: ranges });
      await fetchRanges();
    } catch (err) {
      setError(err?.message || 'Failed to save delivery prices.');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen w-full bg-gray-50">
      <FoodDeliveryTabs />
      <div className="px-4 py-6 lg:px-8">
        <div className="mx-auto max-w-3xl space-y-6">
          {/* Header */}
          <div className="bg-white px-6 py-6 rounded-lg shadow-sm border border-gray-200">
            <h1 className="text-2xl font-extrabold text-[#1F2937]">Food Delivery — Delivery Price</h1>
            <p className="mt-1 text-sm text-gray-500">Configure distance-based delivery pricing • {activeInstitution}</p>
          </div>

          {/* Messages */}
          {error && (
            <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              {error}
            </div>
          )}
          {loading && (
            <div className="px-4 py-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-700">
              ⏳ Updating delivery prices...
            </div>
          )}

          {/* Add New Range */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Add New Price Range</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Max Distance (km)</label>
                <input
                  type="number"
                  placeholder="e.g., 5"
                  value={newRange.maxKm}
                  onChange={(e) => setNewRange((p) => ({ ...p, maxKm: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Price (₹)</label>
                <input
                  type="number"
                  placeholder="e.g., 30"
                  value={newRange.price}
                  onChange={(e) => setNewRange((p) => ({ ...p, price: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={addRange}
                  className="w-full px-4 py-2 rounded-lg bg-gray-900 text-white font-semibold hover:bg-gray-800 transition"
                >
                  + Add Range
                </button>
              </div>
            </div>
          </div>

          {/* Price Ranges List */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Current Price Ranges ({ranges.length})</h2>
            {ranges.length === 0 && !loading ? (
              <div className="rounded-lg border border-dashed border-gray-300 px-4 py-8 text-center text-gray-500">
                No price ranges configured yet. Add your first range above.
              </div>
            ) : (
              <div className="space-y-2">
                {ranges.map((range, idx) => (
                  <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 transition group">
                    <div className="flex items-center gap-4 flex-1">
                      <div className="flex items-center gap-2 font-semibold text-gray-900">
                        <span className="px-3 py-1 rounded-full bg-gray-200 text-xs font-bold">{idx + 1}</span>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">
                          {range.minKm} km - {range.maxKm} km
                        </p>
                        <p className="text-sm text-gray-600">Price: ₹{Number(range.price || 0).toFixed(2)}</p>
                      </div>
                    </div>
                    {ranges.length > 1 && (
                      <button
                        onClick={() => removeRange(idx)}
                        className="px-3 py-2 rounded-lg bg-red-50 text-red-600 font-semibold hover:bg-red-100 opacity-0 group-hover:opacity-100 transition"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Save Button */}
          {ranges.length > 0 && (
            <div className="flex gap-3">
              <button
                onClick={save}
                disabled={loading}
                className="flex-1 px-6 py-3 rounded-lg bg-gray-900 text-white font-semibold hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                {loading ? "💾 Saving..." : "💾 Save Delivery Prices"}
              </button>
            </div>
          )}

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="font-semibold text-blue-900 mb-2">ℹ️ How Pricing Works</h3>
            <ul className="text-sm text-blue-800 space-y-2">
              <li>• Ranges are automatically calculated: First range starts from 0 km</li>
              <li>• Each new range starts where the previous one ended</li>
              <li>• Example: 0-5km = ₹30, 5-10km = ₹50, 10+ km = ₹70</li>
              <li>• All ranges must have unique distance limits</li>
              <li>• Changes are saved immediately to the system</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeliveryPrice;
